import classNames from 'classnames';
import React, { UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from 'react-icons/io';
import { FilterValue, HeaderGroup, IdType, Row, TableInstance } from 'react-table';

import { ComponentSizes } from 'models/general';
import MIcon from 'views/MIcon/MIcon';
import MSpinner from 'views/MSpinner/MSpinner';

import NoResultDark from 'assets/images/noResultDark.svg?react';
import NoResultLight from 'assets/images/noResultLight.svg?react';
import MTableRow from './MTableRow/MTableRow';
import { FilterMetadata, RowData } from './Table.model';
import classes from './Table.module.scss';

type Props<T extends RowData> = {
  table: TableInstance<T>;
  scrollTableRef?: React.RefObject<HTMLDivElement>;
  disableFilters?: boolean;
  noResultsClassName?: string;
  noResultsText?: string;
  noResultsSubHeaderText?: string;
  rowClassName?: string;
  loading: boolean;
  className?: string;
  hideSelection?: boolean;
  hiddenColumns: string[];
  hiddenHeader?: boolean;
  darkMode: boolean;
  columnCenter?: boolean;
  selectedTable?: boolean;
  hideNoResultDarkIcon?: boolean;
  noResultImage?: string;
  isDraggable?: boolean;
  onFilterChange: (field: IdType<T>, value: FilterValue, metadata?: FilterMetadata) => void;
  isRowSelected: (row: Row<T>) => boolean;
  onHeaderClick: (name: string) => void;
  renderExpand?: (row: Row<T>) => React.ReactElement | null;
  onRowClick?: (row: Row<T>) => void;
  onScrollContainer: (e: UIEvent<HTMLDivElement>) => void;
  onMoveRow?: (fromIndex: number, toIndex: number) => void;
};

const MTableView = <T extends RowData>(props: React.PropsWithChildren<Props<T>>): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div
      ref={props.scrollTableRef}
      className={`position-relative table-responsive ${classes['container']} ${props.className}`.trim()}
      onScroll={props.onScrollContainer}
    >
      {props.loading ? (
        <div className={classes['loading']}>
          <MSpinner size={ComponentSizes.Medium}></MSpinner>
        </div>
      ) : props.table.rows.length ? (
        <table
          className={`table-striped table-hover m-0 ${classes['table']} ${
            props.loading || props.table.rows.length <= 0 ? classes['table--maxHeightSpace'] : ''
          }`.trim()}
          {...props.table.getTableProps()}
        >
          {props.table.rows.length && !props.hiddenHeader ? (
            <thead>
              {props.table.headerGroups.map((headerGroup: HeaderGroup<T>) => {
                const { key, ...rest } = headerGroup.getHeaderGroupProps();

                return (
                  <tr key={key} {...rest} className={classes['theadContent']}>
                    {headerGroup.headers
                      .filter((header: HeaderGroup<T>) => !props.hiddenColumns.includes(header.id))
                      .map((column: HeaderGroup<T>, index: number) => {
                        const { key, ...rest } = column.getHeaderProps(column.getSortByToggleProps());
                        let thStyle: React.CSSProperties = {};

                        if (column.width) {
                          thStyle = {
                            width: column.width,
                            maxWidth: column.width,
                            minWidth: column.width,
                          };
                        }

                        return (
                          <th
                            key={key}
                            {...rest}
                            onClick={(event: React.MouseEvent<HTMLTableHeaderCellElement>) => {
                              if (!column.canSort) {
                                return;
                              }

                              rest['onClick'](event);
                              props.onHeaderClick(column.id);
                            }}
                            style={thStyle}
                            className={classNames(classes['header'], { [classes['header--checked']]: index === 0 })}
                          >
                            <div className={classes['headerContainer']}>
                              <div
                                className={classNames(classes['headerContent'], {
                                  [classes['headerContent--center']]: props.columnCenter,
                                })}
                              >
                                <span className={classes['headerTextUppercase']}>{column.render('Header')}</span>
                                <span>
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <MIcon name={IoIosArrowRoundDown}></MIcon>
                                    ) : (
                                      <MIcon name={IoIosArrowRoundUp}></MIcon>
                                    )
                                  ) : null}
                                </span>
                              </div>
                            </div>
                          </th>
                        );
                      })}
                  </tr>
                );
              })}
              {!props.disableFilters &&
                props.table.headerGroups.map((headerGroup: HeaderGroup<T>) => {
                  const { key, ...rest } = headerGroup.getHeaderGroupProps();

                  return (
                    <tr key={key} {...rest}>
                      {headerGroup.headers.map((column: HeaderGroup<T>) => {
                        const { key, ...rest } = column.getHeaderProps();

                        return (
                          <td key={key} {...rest} className={classes['filterCell']}>
                            {column.canFilter
                              ? column.render('Filter', {
                                  onChange: (value: FilterValue, metadata?: FilterMetadata) =>
                                    props.onFilterChange(column.id, value, metadata),
                                })
                              : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </thead>
          ) : null}
          <tbody className={classes['tbodyContent']} {...props.table.getTableBodyProps()}>
            {props.table.rows.length ? (
              <tr>
                <td colSpan={props.table.columns.length} className={classes['tbodyContent--start']}></td>
              </tr>
            ) : null}
            {props.table.rows.map((row: Row<T>, i: number) => {
              return (
                <MTableRow
                  key={row.id}
                  index={i}
                  isRowSelected={props.isRowSelected}
                  onRowClick={props.onRowClick}
                  rowClassName={props.rowClassName}
                  table={props.table}
                  row={row}
                  hiddenColumns={props.hiddenColumns}
                  onMoveRow={props.onMoveRow}
                  isDraggable={props.isDraggable}
                  columnCenter={props.columnCenter}
                ></MTableRow>
              );
            })}
            {props.table.rows.length ? (
              <tr className={classes['tbodyContent--end']}>
                <td colSpan={props.table.columns.length} className={classes['tbodyContent--end']}></td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : (
        <div className={`${classes['noResults']} ${props.noResultsClassName}`}>
          {!props.hideNoResultDarkIcon ? (
            props.noResultImage ? (
              <img src={props.noResultImage} className={classes['noResultsImg']} />
            ) : props.darkMode ? (
              <NoResultDark className={classes['noResultsImg']}></NoResultDark>
            ) : (
              <NoResultLight className={classes['noResultsImg']}></NoResultLight>
            )
          ) : null}

          <span className={classes['noResultsHeader']}>{props.noResultsText || t('views.table.noResults')}</span>
          <span className={classes['noResultsSubHeader']}>{props.noResultsSubHeaderText || t('views.table.noResultsSubHeader')}</span>
        </div>
      )}
    </div>
  );
};

MTableView.displayName = 'MTableView';

export default MTableView;
